import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExportReasonResource } from './types';
interface ReasonResourcesProps {
  resources: ExportReasonResource[];
  className?: string;
}

/**
 * Renders a compact list of linked resources for a reason. Internal links use
 * the Next.js <Link> component; external links open in a new tab.
 */
export function ReasonResources(t0) {
  const $ = _c(10);
  const {
    resources,
    className
  } = t0;
  if (resources.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("space-y-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Drill deeper</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== resources) {
    t3 = resources.map(_temp);
    $[3] = resources;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <ul className="space-y-1.5">{t3}</ul>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp(resource) {
  return <li key={resource.id}><ResourceLink resource={resource} /></li>;
}
function ResourceLink(t0) {
  const $ = _c(18);
  const {
    resource
  } = t0;
  let t1;
  if ($[0] !== resource.external) {
    t1 = <span className="mt-0.5 shrink-0 text-teal-600">{resource.external ? <ExternalLink className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}</span>;
    $[0] = resource.external;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== resource.label) {
    t2 = <span className="text-sm font-medium text-gray-800 group-hover:text-teal-700">{resource.label}</span>;
    $[2] = resource.label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== resource.description) {
    t3 = resource.description && <span className="block text-xs text-gray-500">{resource.description}</span>;
    $[4] = resource.description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span>{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <span className="flex items-start gap-2">{t1}{t4}</span>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const content = t5;
  if (resource.external) {
    let t6;
    if ($[12] !== content || $[13] !== resource.href) {
      t6 = <a href={resource.href} target="_blank" rel="noopener noreferrer" className="group block rounded-lg border border-gray-200 bg-white px-3 py-2 transition-colors hover:border-teal-300 hover:bg-teal-50/50 cursor-pointer">{content}</a>;
      $[12] = content;
      $[13] = resource.href;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  let t6;
  if ($[15] !== content || $[16] !== resource.href) {
    t6 = <Link href={resource.href} className="group block rounded-lg border border-gray-200 bg-white px-3 py-2 transition-colors hover:border-teal-300 hover:bg-teal-50/50 cursor-pointer">{content}</Link>;
    $[15] = content;
    $[16] = resource.href;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}